import React from 'react';
import { Languages } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../Card';
import { Badge } from '../Badge';
import type { LocaleSignalSummary, LocaleValueRow } from '../../types/traffic';

/** Plain-language names for the raw values the browser reports. */
const CALENDAR_LABELS: Record<string, string> = {
  gregory: 'Gregorian',
  persian: 'Persian (Solar Hijri)',
  'islamic-umalqura': 'Islamic (Umm al-Qura)',
  'islamic-civil': 'Islamic (civil)',
  islamic: 'Islamic',
  buddhist: 'Buddhist',
  japanese: 'Japanese imperial',
  roc: 'Minguo (ROC)',
  ethiopic: 'Ethiopic',
  hebrew: 'Hebrew'
};
const NUMBERING_LABELS: Record<string, string> = {
  latn: 'Latin (0–9)',
  arab: 'Arabic-Indic (٠١٢)',
  arabext: 'Persian (۰۱۲)',
  beng: 'Bengali (০১২)',
  deva: 'Devanagari (०१२)',
  thai: 'Thai (๐๑๒)',
  mymr: 'Burmese (၀၁၂)',
  khmr: 'Khmer',
  guru: 'Gurmukhi',
  taml: 'Tamil'
};

/** Values that indicate nothing regional — the overwhelming default. */
const NEUTRAL = new Set(['gregory', 'latn']);
const BASIS_LABELS: Record<string, string> = {
  'secondary language': 'a secondary accepted language',
  calendar: 'the calendar system',
  'digit system': 'the digit system'
};
interface LocaleSignalsCardProps {
  signals: LocaleSignalSummary;
  uniqueVisitors: number;
}
function ValueList({
  rows,
  labels,
  total
}: {
  rows: LocaleValueRow[];
  labels: Record<string, string>;
  total: number;
}) {
  if (rows.length === 0) {
    return <p className="mt-2 text-xs text-muted-foreground" data-id="element-120" data-mp="1v9ytfw1sry5e">Not reported yet</p>;
  }
  return <ul className="mt-2 space-y-1.5" data-id="element-121" data-mp="298yfcvxxdyxv">
      {rows.map(row => {
      const neutral = NEUTRAL.has(row.value);
      const percent = total > 0 ? Math.round(row.visitors / total * 100) : 0;
      return <li key={row.value} className="flex items-baseline justify-between gap-3 text-xs" data-id="element-122" data-mp="eunu0xgjhmwu">
            <span className={neutral ? 'text-muted-foreground' : 'font-medium text-foreground'} data-id="element-123" data-mp="au3tdjwrc15y">
              {labels[row.value] ?? row.value}
              {!neutral && <span className="ml-1.5 font-normal text-muted-foreground" data-id="element-124" data-mp="2exgkryhe27s8">regional</span>}
            </span>
            <span className="whitespace-nowrap font-mono tabular-nums text-muted-foreground" data-id="element-125" data-mp="2jw07i13t3nln">
              {row.visitors.toLocaleString()}
              <span className="ml-1.5" data-id="element-126" data-mp="2ox7fo19v2osu">{percent}%</span>
            </span>
          </li>;
    })}
    </ul>;
}

/**
 * Regional settings buried in OS preferences rather than browser menus: the
 * calendar, the digit system, and the full accepted-language list. These survive a
 * changed time zone, which is what makes them useful where the zone reads UTC.
 */
export function LocaleSignalsCard({
  signals,
  uniqueVisitors
}: LocaleSignalsCardProps) {
  const {
    withSignals,
    tension,
    calendars,
    numbering,
    secondaryLanguages,
    byOrigin,
    byBasis
  } = signals;
  const coverage = uniqueVisitors > 0 ? Math.min(100, Math.round(withSignals / uniqueVisitors * 100)) : 0;
  const hasData = withSignals > 0;
  const lowSample = withSignals < 30;
  const regionalCalendars = calendars.filter(row => !NEUTRAL.has(row.value));
  const regionalDigits = numbering.filter(row => !NEUTRAL.has(row.value));
  const resolvedOnlyByLocale = byBasis.reduce((sum, row) => sum + row.visitors, 0);
  return <Card data-id="element-127" data-mp="r18rld5wtw4i">
      <CardHeader data-id="element-128" data-mp="r1igexf7xx8m">
        <div className="flex flex-wrap items-center justify-between gap-2" data-id="element-129" data-mp="r20joe0atbq1">
          <CardTitle className="flex items-center gap-2" data-id="element-130" data-mp="1ommtbx6dlj40">
            <Languages className="h-4 w-4 text-muted-foreground" aria-hidden="true" data-id="element-131" data-mp="1jo38y17fkfdr" />
            Locale internals
          </CardTitle>
          <div className="flex items-center gap-2" data-id="element-132" data-mp="1evs9t0kikxm9">
            {hasData && lowSample && <Badge variant="outline" data-id="element-133" data-mp="19xqpkcbcqs97">Low sample</Badge>}
            <Badge variant="outline" data-id="element-134" data-mp="27dxmkkzkq7z2">Survives spoofing</Badge>
          </div>
        </div>
        <CardDescription data-id="element-135" data-mp="22n7f927l7v1q">
          Three settings that live in OS regional preferences rather than a browser menu — so they
          usually stay put when someone changes their time zone or interface language
        </CardDescription>
      </CardHeader>
      <CardContent data-id="element-136" data-mp="1r7kl5llmsmty">
        {hasData ? <>
            <div className="rounded-md border border-border bg-muted/40 p-3 text-xs leading-relaxed text-muted-foreground" data-id="element-137" data-mp="1d79uojo8ha9b">
              <span className="font-medium text-foreground" data-id="element-138" data-mp="1i75zgl4r95px">What this is measuring.</span> Of{' '}
              {withSignals.toLocaleString()} visitors whose browser reported these settings (
              {coverage}% of all visitors),{' '}
              <span className="font-medium text-foreground" data-id="element-139" data-mp="2awuzow9n51qh">
                {tension.toLocaleString()} show a mismatch
              </span>{' '}
              between their interface language and their underlying regional settings — for example
              an English interface with Persian digits, or{' '}
              <span className="font-mono" data-id="element-140" data-mp="2osqbyj9jbekw">en-US</span> as the primary language while{' '}
              <span className="font-mono" data-id="element-141" data-mp="2tncsf0uh3xbu">fa</span> is still accepted.{' '}
              {resolvedOnlyByLocale > 0 ? <>
                  <span className="font-medium text-foreground" data-id="element-142" data-mp="d7psah07dn9y">
                    {resolvedOnlyByLocale.toLocaleString()} visitors were placed in a region by these
                    settings alone
                  </span>{' '}
                  — their time zone gave nothing away.
                </> : <>
                  So far every visitor was already placed by their time zone, so these settings have
                  not yet added anyone new.
                </>}
            </div>

            <div className="mt-6 grid gap-6 sm:grid-cols-3" data-id="element-143" data-mp="89elyg949uwh">
              <div data-id="element-144" data-mp="38quxukw1e29">
                <p className="text-xs font-medium text-foreground" data-id="element-145" data-mp="388tmjjdc9nh">Calendar system</p>
                <p className="mt-0.5 text-xs text-muted-foreground" data-id="element-146" data-mp="1jm8ovh79rb4e">
                  {regionalCalendars.length > 0 ? `${regionalCalendars.length} non-Gregorian in use` : 'All Gregorian so far'}
                </p>
                <ValueList rows={calendars} labels={CALENDAR_LABELS} total={withSignals} data-id="element-147" data-mp="h9u9t1ni2nen" />
              </div>
              <div data-id="element-148" data-mp="lbp960lveqps">
                <p className="text-xs font-medium text-foreground" data-id="element-149" data-mp="lb7fnbqleqlk">Digit system</p>
                <p className="mt-0.5 text-xs text-muted-foreground" data-id="element-150" data-mp="qaaak3cuzgft">
                  {regionalDigits.length > 0 ? `${regionalDigits.length} non-Latin in use` : 'All Latin digits so far'}
                </p>
                <ValueList rows={numbering} labels={NUMBERING_LABELS} total={withSignals} data-id="element-151" data-mp="3trsfa1xc47c0" />
              </div>
              <div data-id="element-152" data-mp="2jmxl3o360me">
                <p className="text-xs font-medium text-foreground" data-id="element-153" data-mp="7bo8om9iplau">Secondary languages</p>
                <p className="mt-0.5 text-xs text-muted-foreground" data-id="element-154" data-mp="7diwc8c6brim">
                  Accepted, but not the interface language
                </p>
                <ValueList rows={secondaryLanguages} labels={{}} total={withSignals} data-id="element-155" data-mp="3azpwol14ugz4" />
              </div>
            </div>

            {byOrigin.length > 0 && <div className="mt-6 border-t border-border pt-4" data-id="element-156" data-mp="2yi5nl9w1po5k">
                <p className="text-xs font-medium text-foreground" data-id="element-157" data-mp="2yiymxpsodbdo">
                  Where the {tension.toLocaleString()} mismatched visitors resolve to
                </p>
                <p className="mt-0.5 text-xs text-muted-foreground" data-id="element-158" data-mp="2pgfpxmbklszc">
                  Region, and which signal placed them there.
                </p>
                <ul className="mt-3 space-y-1.5" data-id="element-159" data-mp="2frnzxjq2lir2">
                  {byOrigin.map(row => <li key={`${row.origin}-${row.basis}`} className="flex items-baseline justify-between gap-3 text-xs" data-id="element-160" data-mp="2fpupdtcc6eds">
                      <span className="text-foreground" data-id="element-161" data-mp="25rf0lx0s7h7g">
                        {row.origin}{' '}
                        <span className="text-muted-foreground" data-id="element-162" data-mp="21tzcq58j5ziu">
                          via {BASIS_LABELS[row.basis] ?? row.basis}
                        </span>
                      </span>
                      <span className="font-mono tabular-nums text-muted-foreground" data-id="element-163" data-mp="3kqay38bh8jfe">
                        {row.visitors.toLocaleString()}{' '}
                        {row.visitors === 1 ? 'visitor' : 'visitors'}
                      </span>
                    </li>)}
                </ul>
              </div>}

            {lowSample && <p className="mt-4 text-xs leading-relaxed text-muted-foreground" data-id="element-164" data-mp="31y8ate4by4w6">
                <span className="font-medium text-foreground" data-id="element-165" data-mp="36q00znjkq8ls">Too few to read as a rate.</span> Only{' '}
                {withSignals.toLocaleString()} visitors have reported these settings, because they
                are recorded on views written after this was added. Treat the individual rows above
                as observations, not the percentage as a trend.
              </p>}
          </> : <p className="text-xs leading-relaxed text-muted-foreground" data-id="element-166" data-mp="2nyperjosmk08">
            No locale settings recorded yet. They are captured on views written after this was added,
            and cannot be backfilled — the values only exist in the visitor&rsquo;s browser at the
            moment of the request.
          </p>}

        <div className="mt-6 space-y-2 border-t border-border pt-3 text-xs leading-relaxed text-muted-foreground" data-id="element-167" data-mp="2dbsirg52vmsc">
          <p data-id="element-168" data-mp="2nc1ih3ya3qch">
            <span className="font-medium text-foreground" data-id="element-169" data-mp="2nbrnt32agv0g">Why these three.</span> Tor Browser forces
            the time zone to UTC and the language to en-US, but does not normalise the calendar or
            digit system — so a Persian calendar or Persian digits still point at a region. And
            switching an interface to English usually leaves the original language listed as still
            accepted.
          </p>
          <p data-id="element-170" data-mp="363kl39oe52dz">
            <span className="font-medium text-foreground" data-id="element-171" data-mp="3632fvnjr3dtx">Why it is only a signal.</span> People
            legitimately prefer another calendar or keyboard, expatriates keep their home settings,
            and anyone deliberately evading detection can change these too. Directional at aggregate
            scale; never evidence about a person.
          </p>
        </div>
      </CardContent>
    </Card>;
}